import { useState } from "react";
import { AlertTriangle } from "lucide-react";

import { PoliciesService, type PolicyRuleResponse } from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";

interface PolicyDeleteDialogProps {
  rule: PolicyRuleResponse | null;
  onClose: () => void;
  onSuccess: () => void;
}

export function PolicyDeleteDialog({
  rule,
  onClose,
  onSuccess,
}: PolicyDeleteDialogProps) {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState("");

  const { mutate, isLoading, error, reset } = useMutation<void, void>(
    async () => {
      if (rule) await PoliciesService.delete(rule.id);
    },
    {
      onSuccess: () => {
        setConfirmName("");
        onSuccess();
      },
    }
  );

  if (!rule) return null;

  const matches = confirmName.trim() === rule.name;

  const handleClose = () => {
    reset();
    setConfirmName("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!matches) return;
    await mutate();
  };

  return (
    <Dialog open onClose={handleClose} size="max-w-md">
      <DialogHeader>
        <DialogTitle>{t("policies.deleteDialog.title")}</DialogTitle>
        <DialogDescription>
          {t("policies.deleteDialog.descriptionBefore")}
          <strong className="text-ocean-900">{rule.name}</strong>
          {t("policies.deleteDialog.descriptionMiddle")}
          <strong className="text-danger-600">
            {t("policies.deleteDialog.descriptionIrreversible")}
          </strong>
          {t("policies.deleteDialog.descriptionAfter")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <Alert variant="danger">
            <div className="flex items-start gap-2 text-xs">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <div>
                {t("policies.deleteDialog.confirmInstruction")}{" "}
                <strong className="font-mono">{rule.name}</strong>
              </div>
            </div>
          </Alert>
          <FormField
            label={t("policies.deleteDialog.confirmLabel")}
            value={confirmName}
            onChange={setConfirmName}
            placeholder={rule.name}
            error={
              confirmName.length > 0 && !matches
                ? t("policies.deleteDialog.confirmError")
                : undefined
            }
            required
          />
          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("policies.deleteDialog.cancel")}
          </Button>
          <Button
            type="submit"
            variant="danger"
            disabled={isLoading || !matches}
          >
            {isLoading
              ? t("policies.deleteDialog.submitLoading")
              : t("policies.deleteDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
